import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import authService from '../services/authService';
import { useCity } from '../contexts/CityContext';

const POPULAR_CITIES = [
  'Mumbai', 'Delhi', 'Bengaluru', 'Hyderabad', 'Pune',
  'Chennai', 'Kolkata', 'Ahmedabad', 'Jaipur', 'Surat',
];

function CityPicker({ onClose }) {
  const { city, setCity } = useCity();
  const [input, setInput] = useState(city);

  const pick = (c) => { setCity(c); onClose(); };

  return (
    <div
      className="fixed inset-0 bg-black/50 z-[100] flex items-start justify-center pt-20"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-bold text-gray-900">Select Your City</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="flex gap-2 mb-5">
          <input
            type="text"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && input.trim() && pick(input.trim())}
            placeholder="Type city name…"
            className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            autoFocus
          />
          <button
            onClick={() => input.trim() && pick(input.trim())}
            className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-semibold hover:bg-primary/90"
          >
            Go
          </button>
        </div>
        <p className="text-xs text-gray-400 uppercase font-semibold mb-3">Popular Cities</p>
        <div className="flex flex-wrap gap-2">
          {POPULAR_CITIES.map(c => (
            <button
              key={c}
              onClick={() => pick(c)}
              className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                city === c
                  ? 'bg-primary text-white border-primary'
                  : 'border-gray-200 text-gray-700 hover:border-primary hover:text-primary'
              }`}
            >
              {c}
            </button>
          ))}
        </div>
        {city && (
          <button
            onClick={() => pick('')}
            className="mt-4 w-full text-sm text-gray-400 hover:text-red-500 transition-colors"
          >
            Clear city filter
          </button>
        )}
      </div>
    </div>
  );
}

function Navbar() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showCityPicker, setShowCityPicker] = useState(false);
  const isAuthenticated = authService.isAuthenticated();
  const user = authService.getCurrentUser();
  const { city } = useCity();

  const handleLogout = async () => {
    await authService.logout();
    window.location.href = '/';
  };

  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  return (
    <>
    <nav className="navbar bg-base-100 shadow-lg sticky top-0 z-50 px-4">
      <div className="navbar-start">
        {/* Logo */}
        <Link to="/" className="btn btn-ghost normal-case text-2xl font-bold text-primary">
          BoardVoyage
        </Link>
      </div>

      <div className="navbar-center hidden lg:flex items-center gap-1">
        {/* City Selector */}
        <button
          onClick={() => setShowCityPicker(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-gray-200 text-sm font-medium text-gray-700 hover:border-primary hover:text-primary transition-colors mr-2"
        >
          <svg className="w-3.5 h-3.5 text-primary" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
          </svg>
          <span className="max-w-[90px] truncate">{city || 'All Cities'}</span>
          <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {/* Navigation Links - Desktop */}
        <ul className="menu menu-horizontal px-1">
          <li><Link to="/">Home</Link></li>
          <li><Link to="/find-clubs">Clubs</Link></li>
          <li><Link to="/events">Events</Link></li>
          <li><Link to="/board-games">Board Games</Link></li>
          <li><Link to="/discussions">Discussions</Link></li>
          <li><Link to="/pricing">Pricing</Link></li>
          <li><Link to="/partner-with-us">Partner With Us</Link></li>
        </ul>
      </div>

      <div className="navbar-end">
        {/* Auth Buttons - Desktop */}
        <div className="hidden lg:flex items-center gap-2">
          {isAuthenticated ? (
            <>
              <span className="text-base-content mr-2 whitespace-nowrap">
                Hi, {user?.first_name || 'User'}
              </span>
              {user?.roles?.includes('super_admin') && (
                <Link to="/super-admin" className="btn btn-ghost btn-sm text-purple-600 hover:text-purple-700">
                  Super Admin
                </Link>
              )}
              {user?.roles?.includes('club_owner') && (
                <Link to="/club-management" className="btn btn-ghost btn-sm text-primary">
                  Club Management
                </Link>
              )}
              <button onClick={handleLogout} className="btn btn-ghost btn-sm">Logout</button>
            </>
          ) : (
            <>
              <Link to="/login" className="btn btn-ghost btn-sm text-primary">Sign In</Link>
              <Link to="/register" className="btn btn-primary btn-sm">Sign Up</Link>
            </>
          )}
        </div>

        {/* Mobile Controls */}
        <div className="lg:hidden flex items-center gap-2">
          <button
            onClick={() => setShowCityPicker(true)}
            className="flex items-center gap-1 px-2.5 py-1 rounded-full border border-gray-200 text-xs font-medium text-gray-700 hover:border-primary"
          >
            <svg className="w-3 h-3 text-primary" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
            </svg>
            {city || 'City'}
          </button>
          <button
            className="btn btn-square btn-ghost"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="absolute top-full left-0 w-full bg-base-100 shadow-lg lg:hidden z-40">
          <ul className="menu menu-vertical w-full rounded-box">
            <li><Link to="/" onClick={closeMobileMenu}>Home</Link></li>
            <li><Link to="/find-clubs" onClick={closeMobileMenu}>Clubs</Link></li>
            <li><Link to="/events" onClick={closeMobileMenu}>Events</Link></li>
            <li><Link to="/board-games" onClick={closeMobileMenu}>Board Games</Link></li>
            <li><Link to="/discussions" onClick={closeMobileMenu}>Discussions</Link></li>
            <li><Link to="/pricing" onClick={closeMobileMenu}>Pricing</Link></li>
            <li><Link to="/partner-with-us" onClick={closeMobileMenu}>Partner With Us</Link></li>

            <li className="menu-title mt-2"><span>Account</span></li>

            {isAuthenticated ? (
              <>
                {user?.roles?.includes('super_admin') && (
                  <li><Link to="/super-admin" className="text-purple-600" onClick={closeMobileMenu}>Super Admin</Link></li>
                )}
                {user?.roles?.includes('club_owner') && (
                  <li><Link to="/club-management" className="text-primary" onClick={closeMobileMenu}>Club Management</Link></li>
                )}
                <li><Link to="/dashboard" onClick={closeMobileMenu}>Dashboard</Link></li>
                <li><button onClick={() => { handleLogout(); closeMobileMenu(); }}>Logout</button></li>
              </>
            ) : (
              <>
                <li><Link to="/login" onClick={closeMobileMenu}>Sign In</Link></li>
                <li><Link to="/register" className="btn btn-primary btn-sm" onClick={closeMobileMenu}>Sign Up</Link></li>
              </>
            )}
          </ul>
        </div>
      )}
    </nav>
    {showCityPicker && <CityPicker onClose={() => setShowCityPicker(false)} />}
    </>
  );
}

export default Navbar;
